import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import last from "lodash/last";
import isInteger from "lodash/isInteger";
import moment from "moment";
import styled from "styled-components";
import {
  ControlLabel,
  InputWrapper,
  FormControl,
  DropdownButton,
  HelpBlock,
  FormGroup,
  MenuItem,
  InputGroup
} from "components/bootstrap";
import { getValueFromInput } from "util/FormsUtils";
export const extractDurationAndUnit = (duration, timeUnits) => {
  if (duration === void 0) {
    return {
      duration: 1,
      unit: last(timeUnits)
    };
  }
  const momentDuration = moment.duration(duration);
  const timeUnit = timeUnits.find((unit) => {
    const durationInUnit2 = momentDuration.as(unit);
    return isInteger(durationInUnit2) && durationInUnit2 !== 0;
  }) || last(timeUnits);
  const durationInUnit = momentDuration.as(timeUnit);
  return {
    duration: durationInUnit,
    unit: timeUnit
  };
};
const StyledInputGroup = styled(InputGroup)`
  display: flex;
`;
const InputGroupAddon = styled(InputGroup.Addon)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const unitValues = [
  "NANOSECONDS",
  "MICROSECONDS",
  "MILLISECONDS",
  "SECONDS",
  "MINUTES",
  "HOURS",
  "DAYS",
  "MONTHS",
  "YEARS"
];
const defaultUnits = [
  "NANOSECONDS",
  "MICROSECONDS",
  "MILLISECONDS",
  "SECONDS",
  "MINUTES",
  "HOURS",
  "DAYS"
];
const UNIT_LABELS = {
  NANOSECONDS: "time_units.nanoseconds",
  MICROSECONDS: "time_units.microseconds",
  MILLISECONDS: "time_units.milliseconds",
  SECONDS: "time_units.seconds",
  MINUTES: "time_units.minutes",
  HOURS: "time_units.hours",
  DAYS: "time_units.days",
  MONTHS: "time_units.months",
  YEARS: "time_units.years"
};
const TimeUnitInput = ({
  update,
  value = void 0,
  unit = "SECONDS",
  enabled = void 0,
  defaultEnabled = false,
  disabled = false,
  label = "",
  help = "",
  name = null,
  required = false,
  clearable = false,
  defaultValue = 1,
  pullRight = false,
  hideCheckbox = false,
  units = defaultUnits,
  unitName = null,
  wrapperClassName = "",
  labelClassName = ""
}) => {
  var _a, _b;
  const { t } = useTranslation("common");
  const unitOptions = unitValues.filter((option) => units.includes(option)).map((option) => ({ value: option, label: t(UNIT_LABELS[option]) }));
  const [checked, setChecked] = useState(enabled != null ? enabled : defaultEnabled);
  const getEffectiveValue = () => clearable ? value : value != null ? value : defaultValue;
  const getCheckedValue = () => required || checked;
  const propagateChange = (change) => {
    const prevValue = {
      value: getEffectiveValue(),
      unit,
      checked: getCheckedValue()
    };
    const nextValue = {
      ...prevValue,
      ...change
    };
    update(nextValue.value, nextValue.unit, nextValue.checked);
  };
  const handleValueChange = (e) => {
    var _a2;
    if (clearable) propagateChange({ value: getValueFromInput(e.target) });
    else propagateChange({ value: (_a2 = getValueFromInput(e.target)) != null ? _a2 : defaultValue });
  };
  const handleCheckedChange = (e) => {
    setChecked(e.target.checked);
    propagateChange({ checked: e.target.checked });
  };
  return /* @__PURE__ */ React.createElement(FormGroup, null, label && /* @__PURE__ */ React.createElement(ControlLabel, { className: labelClassName }, label), /* @__PURE__ */ React.createElement(InputWrapper, { className: wrapperClassName }, /* @__PURE__ */ React.createElement(StyledInputGroup, null, !required && !hideCheckbox && !disabled && /* @__PURE__ */ React.createElement(InputGroupAddon, null, /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "checkbox",
      checked: getCheckedValue(),
      onChange: handleCheckedChange,
      title: t("time.toggle_time"),
      "aria-label": t("time.toggle_time")
    }
  )), /* @__PURE__ */ React.createElement(
    FormControl,
    {
      type: typeof value === "string" ? "string" : "number",
      name,
      disabled: !getCheckedValue() || disabled,
      "aria-label": label || t("time.time_unit_input"),
      onChange: (e) => handleValueChange(e),
      value: (_a = getEffectiveValue()) != null ? _a : ""
    }
  ), /* @__PURE__ */ React.createElement(
    DropdownButton,
    {
      id: "input-dropdown-addon",
      name: unitName,
      pullRight,
      title: (_b = unitOptions.filter((option) => option.value === unit)[0]) == null ? void 0 : _b.label,
      disabled: !getCheckedValue() || disabled
    },
    unitOptions.map((option) => /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        key: option.value,
        onSelect: () => propagateChange({ unit: option.value }),
        active: unit === option.value
      },
      option.label
    ))
  )), help && /* @__PURE__ */ React.createElement(HelpBlock, null, help)));
};
export default TimeUnitInput;
